/**
 * Générateur — Il résout des problèmes avec des nombres rationnels.
 * Attendu ID : 149 | Niveau : 4ème | Domaine : Nombres (desc 11, O14)
 *
 * Source doc prof (NOMBRE ET CALCUL, cluster 3) : Ex.4 « le tiers du double du
 * sixième du quart de 150 » (fractions emboîtées), village « triple puis perd un
 * tiers » (×3 puis ×2/3 = ×2), Ex.9 aires de rectangles (5/8 × 1/3 de ABCD).
 * Plan validé 2026-07-14 :
 *
 *   Carte « chaine »    — fraction emboîtée d'une quantité (saisie 2 trous)
 *   Carte « reste »     — fraction restante d’une quantité (soustraction, QCM)
 *   Carte « part »      — aire d'un rectangle réduit (produit de fractions, saisie)
 *
 * Arithmétique EXACTE. Résultats de « chaine » toujours en fraction irréductible
 * (dénominateur > 1) ; « part » construit pour tomber sur un entier de cm².
 *
 * Revue 2026-09-22 (audit, décisions prof) :
 *   - « part » : dénominateurs jusqu'à 10 au lieu de 6 (l'exemple du prof,
 *     5/8 × 1/3, était impossible à tirer).
 *   - « evolution » : le distracteur « 1 » (aucune erreur d'élève) est remplacé
 *     par le produit obtenu avec le facteur mal lu (×1/3 quand l'énoncé dit
 *     « perd un tiers »), dédoublonné en valeur contre la bonne réponse ;
 *     boucle de complément bornée.
 *   - Parenthèse de consigne de « chaine » réécrite en phrase.
 *   - Fractions AFFICHÉES (options du QCM et énoncé de « part ») en KaTeX via
 *     fracTex, options et bonne réponse d'un seul geste ; les réponses tapées
 *     restent en texte brut.
 *
 * Revue 2026-09-23 (typographie, règle prof) : la phrase à trous de « chaine »
 * (« … = {{p}}/{{q}} », fraction en texte brut) passe en mode mathématique
 * global, fraction empilée par fracTrouTex, le français en \text{} ; en deux
 * lignes (la phrase, puis « = fraction ») parce que réunies, les plus longues
 * dépassent soixante caractères et se feraient couper à l'impression. Les
 * « cm² » de « part » sont des symboles d'unité, ils restent en texte.
 *
 * Revue 2026-09-23 (cadrage programme, décision prof) : la carte « evolution »
 * (facteur global de deux évolutions successives) est SUPPRIMÉE et remplacée par
 * « reste ». Elle n'était pas de ce niveau : la composition de deux évolutions
 * est l'attendu de 3e 219. Détail du motif et du choix du remplacement au-dessus
 * de la carte. Rappel brouillon IMPÉRATIF sur les trois cartes (RAPPEL_BROUILLON).
 */

import { randomChoice, randInt, makeTexteTrou, makeQcm, makeSaisie, fracTex, fracTrouTex } from '../../../utils';
import { rat, ratAdd, ratSub, ratMul, ratEq, fmtFrac } from './nombres';

const ATTENDU_ID = 149;

const L_CHAINE = 'Calculer une fraction emboîtée d\'une quantité';
const L_RESTE = 'Trouver la fraction qui reste';
const L_PART = 'Calculer l\'aire d\'un rectangle réduit';

const opts = (label) => ({ niveau: '4eme', matiere: 'maths', attendu: ATTENDU_ID, label });

// Rappel brouillon (demande prof 2026-09-23), IMPERATIF pour cet attendu. Aucune
// des cartes ne se fait de tete : « chaine » enchaine jusqu'a quatre facteurs,
// « part » multiplie deux fractions par une aire. L'attendu dit « a la main », et
// sur un probleme l'eleve qui croit devoir repondre de tete repond au hasard.
// Forme maison des rappels du parc (6e 613 a 619, 5e 1044, 1045, 1061, 4e 143 :
// ligne grise en italique) ; tournure imperative et non permissive comme sur 143,
// parce qu'ici poser ses calculs n'est pas une facilite, c'est le geste attendu.
// Place : juste avant la QUESTION, jamais avant l'enonce du probleme.
const RAPPEL_BROUILLON = '<p class="italic text-slate-400">✏️ Pose tes calculs au brouillon pour résoudre.</p>';

// ═════════ Carte « chaine » — « le tiers du double du … de N » (2 trous) ═════════

// Phrase à trous en lignes KaTeX globales (technique de gen_924 et gen_205) :
// une ligne ne s'enclenche en mode maths que si elle est ENTIÈREMENT entre $…$,
// le français passe donc en \text{}. La fraction attendue porte ses deux trous
// (triple accolade décodée par preprocessBraces) ; la réponse tapée reste en
// texte brut (« 25 » puis « 3 »).
const T = (s) => `\\text{${s}}`;

const QOPS = [
    { kw: 'moitié', art: 'la moitié', con: 'de la moitié', f: rat(1, 2) },
    { kw: 'tiers', art: 'le tiers', con: 'du tiers', f: rat(1, 3) },
    { kw: 'quart', art: 'le quart', con: 'du quart', f: rat(1, 4) },
    { kw: 'cinquième', art: 'le cinquième', con: 'du cinquième', f: rat(1, 5) },
    { kw: 'sixième', art: 'le sixième', con: 'du sixième', f: rat(1, 6) },
    { kw: 'double', art: 'le double', con: 'du double', f: rat(2, 1) },
    { kw: 'triple', art: 'le triple', con: 'du triple', f: rat(3, 1) },
];

const genChaine = () => {
    let seq; let N; let res;
    do {
        const n = randInt(3, 4);
        seq = [];
        for (let i = 0; i < n; i++) seq.push(randomChoice(QOPS));
        N = randInt(2, 30) * 6;                          // quantité « ronde »
        res = rat(N, 1);
        for (let i = seq.length - 1; i >= 0; i--) res = ratMul(res, seq[i].f);
    } while (res.q === 1 || res.q > 60 || Math.abs(res.p) > 400);
    const phrase = seq[0].art + seq.slice(1).map(o => ` ${o.con}`).join('') + ` de ${N}`;
    const payload = makeTexteTrou(`$${T(phrase)}$\n$= ${fracTrouTex(`{{${res.p}}}`, `{{${res.q}}}`)}$`,
        RAPPEL_BROUILLON
        + '<p>Calcule, puis donne le résultat en fraction irréductible : le numérateur dans le premier trou, '
        + 'le dénominateur dans le second.</p>'
        + `<p><b>${phrase}</b></p>`,
        { ...opts(L_CHAINE), mode: 'saisie' });
    payload._meta.params = { phrase, res: fmtFrac(res) };
    return payload;
};

// ═════════ Carte « reste » — fraction restante d'une quantité (QCM) ═════════

// Remplace la carte « evolution » (facteur global de deux évolutions
// successives), SUPPRIMÉE le 2026-09-23 sur décision prof. Motif : « évolutions
// successives », « taux d'évolution » et « coefficient multiplicateur »
// n'apparaissent NULLE PART dans les attendus de fin de 4e, ni en Nombres ni en
// Proportionnalité, qui s'y limite à la quatrième proportionnelle, aux formules
// liant deux grandeurs et à la géométrie. Composer deux évolutions en un facteur
// global est un apprentissage NEUF de 3e : c'est l'attendu 219, « Il utilise le
// lien entre pourcentage d'évolution et coefficient multiplicateur », dont une
// carte s'appelle « L'aller-retour en pourcentages n'est pas symétrique ». Ce qui
// a bien été vu en 4e, c'est appliquer UNE évolution (automatisme de 3e 1254) ;
// en composer deux, non.
//
// Elle n'est pas supprimée sèchement : c'était le seul QCM de l'attendu, et un
// attendu sans QCM n'est plus dépouillable en mode classe par QR code. La carte
// qui la remplace comble en plus un trou réel : « chaine » et « part » sont deux
// PRODUITS de fractions, rien dans 149 ne faisait SOUSTRAIRE des rationnels dans
// un problème alors que l'intitulé couvre les quatre opérations.
//
// Les deux fractions portent explicitement sur le MÊME tout (« de la bouteille »
// répété sur les deux) : sans cela, « puis » laisse croire que la seconde
// s'applique à ce qui reste, et la question a deux réponses.

const CONTEXTES_RESTE = [
    { intro: 'Une bouteille de jus est pleine.', verbe: 'boit', de: 'de la bouteille', nom1: 'Léa', nom2: 'Tom' },
    { intro: 'Un gâteau entier est posé sur la table.', verbe: 'mange', de: 'du gâteau', nom1: 'Sami', nom2: 'Nour' },
    { intro: 'Un paquet de bonbons est plein.', verbe: 'prend', de: 'du paquet', nom1: 'Inès', nom2: 'Malo' },
];

const UN = rat(1, 1);

export const REGLES_DISTRACTEURS_QCM = {
    reste_qcm_unique: {
        support: 'deux fractions p/q (q de 2 à 10) prélevées sur le même tout, de somme < 1 et de dénominateurs réduits différents',
        familles: {
            somme_naive: 'additionne numérateurs ET dénominateurs ((p1+p2)/(q1+q2)) avant de retirer du tout : l\'erreur dominante sur la somme de fractions',
            part_prise: 'donne la part prise (f1 + f2) au lieu de ce qui reste : il calcule juste et répond à côté de la question',
            un_seul_retrait: 'n\'enlève qu\'un seul des deux prélèvements (1 − f1, ou 1 − f2)',
        },
        supprimees: {
            denominateur_commun_sans_conversion: 'écarté : 1 − p1/L − p2/L avec L le dénominateur commun reste toujours SUPÉRIEUR à la bonne réponse, donc plausible, mais il fallait choisir trois familles pour tenir le format à quatre choix, et les trois gardées sont plus fréquentes',
        },
        garantie: 'QUATRE choix garantis SANS boucle de complément. Les conditions de tirage rendent { reste, part_prise, 1 − f1, 1 − f2 } deux à deux distincts : dénominateurs réduits différents donne f1 ≠ f2 donc 1 − f1 ≠ 1 − f2 ; somme ≠ 1/2 donne reste ≠ part_prise ; 2f1+f2 ≠ 1 et f1+2f2 ≠ 1 donnent part_prise ≠ 1 − f1 et ≠ 1 − f2 ; reste ≠ 1 − f1 et ≠ 1 − f2 puisque f1 et f2 sont strictement positifs. Il reste donc toujours au moins trois distracteurs valides, et somme_naive, la plus fréquente, passe en tête quand elle ne tombe pas sur une valeur déjà présente.',
    },
};

const genReste = () => {
    const ctx = randomChoice(CONTEXTES_RESTE);
    let f1 = rat(1, 4);                                    // repli déterministe si la
    let f2 = rat(1, 3);                                    // boucle bornée n'aboutit pas
    for (let i = 0; i < 200; i++) {
        const q1 = randInt(2, 10);
        const q2 = randInt(2, 10);
        const g1 = rat(randInt(1, q1 - 1), q1);
        const g2 = rat(randInt(1, q2 - 1), q2);
        if (g1.q === g2.q) continue;                       // un dénominateur commun à trouver
        const s = ratAdd(g1, g2);
        if (s.p >= s.q) continue;                          // il doit rester quelque chose
        if (ratEq(s, rat(1, 2))) continue;                 // reste ≠ part prise
        if (ratEq(ratAdd(s, g1), UN)) continue;            // part prise ≠ 1 − f1
        if (ratEq(ratAdd(s, g2), UN)) continue;            // part prise ≠ 1 − f2
        f1 = g1;
        f2 = g2;
        break;
    }
    const prise = ratAdd(f1, f2);
    const reste = ratSub(UN, prise);
    // Ordre de priorité des familles, puis dédoublonnage EN VALEUR contre la
    // bonne réponse et entre elles (toutes les fractions sont réduites).
    const cand = [
        { r: ratSub(UN, rat(f1.p + f2.p, f1.q + f2.q)), fam: 'somme_naive' },
        { r: prise, fam: 'part_prise' },
        { r: ratSub(UN, f1), fam: 'un_seul_retrait' },
        { r: ratSub(UN, f2), fam: 'un_seul_retrait' },
    ];
    const choix = [reste];
    const familles = [];
    const vus = new Set([fmtFrac(reste)]);
    cand.forEach(({ r, fam }) => {
        const cle = fmtFrac(r);
        if (choix.length >= 4 || vus.has(cle)) return;
        vus.add(cle);
        choix.push(r);
        familles.push(fam);
    });
    // Options et bonne réponse par le MÊME helper d'affichage.
    const affiche = (r) => fracTex(r.p, r.q);
    const payload = makeQcm(choix.map(affiche), affiche(reste),
        `<p>${ctx.intro}</p>`
        + `<p>${ctx.nom1} ${ctx.verbe} ${affiche(f1)} ${ctx.de}, et ${ctx.nom2} ${ctx.verbe} ${affiche(f2)} ${ctx.de}.</p>`
        + RAPPEL_BROUILLON
        + `<p><b>Quelle fraction ${ctx.de} reste-t-il ?</b></p>`,
        opts(L_RESTE));
    payload._meta.params = {
        f1: fmtFrac(f1), f2: fmtFrac(f2), reste: fmtFrac(reste), distracteurs: familles,
    };
    return payload;
};

// ═════════ Carte « part » — aire d'un rectangle réduit (saisie) ═════════

const propFrac = () => {                                   // a/b réduit, a < b (réduction)
    for (let i = 0; i < 60; i++) {
        const b = randInt(2, 10); const a = randInt(1, b - 1);
        const r = rat(a, b);
        if (r.p === a && r.q === b) return r;               // déjà réduit
    }
    return rat(1, 2);
};

const genPart = () => {
    const f1 = propFrac();
    const f2 = propFrac();
    const k = randInt(2, 6);
    const aire = f1.q * f2.q * k;                          // multiple → résultat entier
    const res = f1.p * f2.p * k;                            // = f1×f2×aire, entier
    const payload = makeSaisie(String(res),
        `<p>Un rectangle a une aire de <b>${aire} cm²</b>.</p>`
        + `<p>On construit un second rectangle dont la longueur vaut <b>${fracTex(f1.p, f1.q)}</b> `
        + `de la longueur du premier, et la largeur <b>${fracTex(f2.p, f2.q)}</b> de sa largeur.</p>`
        + RAPPEL_BROUILLON
        + '<p><b>Quelle est l\'aire du second rectangle, en cm² ?</b></p>',
        { ...opts(L_PART), suffixe: 'cm²' });
    payload._meta.params = { aire, f1: fmtFrac(f1), f2: fmtFrac(f2), res };
    return payload;
};

// ─── Registre ────────────────────────────────────────────────
const GENERATORS = {
    chaine_texte_trou_saisie: genChaine,
    reste_qcm_unique: genReste,
    part_texte: genPart,
};

const FORMAT_LABELS = { texte_trou_saisie: 'Saisie trous', qcm_unique: 'QCM', texte: 'Saisie' };
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'chaine', label: L_CHAINE, formatsList: buildFormats('chaine') },
    { id: 'reste', label: L_RESTE, formatsList: buildFormats('reste') },
    { id: 'part', label: L_PART, formatsList: buildFormats('part') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
